import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Spinner,
  Button,
  Alert,
} from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import useLogger from "../../util/Logger";
import { useBreadcrumb } from "../../data/BreadcrumbHook";
import { useProperty } from "../../data/PropertyHook";
import { useWorkOrders } from "../../data/WorkOrdersHook";

import "./WorkItem.css";
import WorkItemForms from "./WorkItemForms";
import WorkOrderActiveWorkItem from "../workOrder/WorkOrderActiveWorkItem";
import AccessNotes from "../shared/AccessNotes";

function WorkItem() {
  const logger = useLogger();
  const navigate = useNavigate();
  const breadcrumbRepository = useBreadcrumb();
  const propertyRepository = useProperty();
  const workOrdersRepository = useWorkOrders();

  const { propertyId, workOrderId, workItemId } = useParams();
  const [property, setProperty] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [workItem, setWorkItem] = useState(null);

  useEffect(() => {
    const getData = async () => {
      try {
        logger.debug("WorkItem.useEffect", `Getting data`);

        const retrievedProperty = await propertyRepository.getProperty(
          propertyId
        );
        if (!retrievedProperty)
          throw new Error(
            `The property Id '${propertyId}' did not match a property`
          );

        const retrievedWorkOrders = await workOrdersRepository.getWorkOrders(
          retrievedProperty.id
        );
        if (!retrievedWorkOrders)
          throw new Error(
            `Failed to load work orders for property '${retrievedProperty.id}'`
          );

        let retrievedWorkOrder = null;
        const retrievedWorkOrderMatches = retrievedWorkOrders.filter(
          ({ id }) => id === workOrderId
        );
        if (retrievedWorkOrderMatches.length === 1)
          [retrievedWorkOrder] = retrievedWorkOrderMatches;
        if (!retrievedWorkOrder)
          throw new Error(
            `The work order Id '${workOrderId}' did not match a work order on property '${propertyId}'`
          );

        let retrievedWorkItem = null;
        const retrievedWorkItemMatches = retrievedWorkOrder.workItems.filter(
          ({ id }) => id === workItemId
        );
        if (retrievedWorkItemMatches.length === 1)
          [retrievedWorkItem] = retrievedWorkItemMatches;
        if (!retrievedWorkItem)
          throw new Error(
            `The work item id '${workItemId}' did not match a work item on work order '${workOrderId}'`
          );

        setProperty(retrievedProperty);
        setWorkOrder(retrievedWorkOrder);
        setWorkItem(retrievedWorkItem);

        logger.debug("WorkItem.useEffect", `Setting breadcrumb`);
        breadcrumbRepository.setBreadcrumb([
          { route: "/", name: "search" },
          { route: `/property/${propertyId}`, name: "property" },
          {
            route: `/property/${propertyId}/workorder/${workOrderId}`,
            name: "work order",
          },
          {
            route: `/property/${propertyId}/workorder/${workOrderId}/workitem/${workItemId}`,
            name: "work item",
          },
        ]);
      } catch (error) {
        logger.error(
          "WorkItem.useEffect",
          `An error occurred while loading work item '${workItemId}' for work order '${workOrderId}' from property '${propertyId}' ${error}`
        );
        toast.error(
          "An error occurred while loading the work item. Please try again."
        );
        navigate("/");
      }
    };
    getData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const view =
    property === null || workOrder === null || workItem === null ? (
      <Container className="coreContent">
        <Spinner
          animation="border"
          role="status"
          style={{
            marginTop: "200px",
            marginLeft: "50%",
            marginBottom: "200px",
          }}
        >
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    ) : (
      <Container className="coreContent">
        <h2>Work Item</h2>
        <Row className="mb-4">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Property Id:</Form.Label>
          </Col>
          <Col xs={4}>
            <Form.Label>{property.id}</Form.Label>
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Address</Form.Label>
          </Col>
          <Col xs={6}>
            <Form.Control type="text" readOnly value={property.fullAddress} />
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Postcode</Form.Label>
          </Col>
          <Col xs={2}>
            <Form.Control type="text" readOnly value={property.postCode} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Site</Form.Label>
          </Col>
          <Col xs={6}>
            <Form.Control type="text" readOnly value={property.siteName} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={2}>
            <Form.Label className="form-label-heading">Access Notes</Form.Label>
          </Col>
          <Col xs={6}>
            <AccessNotes accessNotes={property.accessNotes} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={2}>
            <Form.Label className="form-label-heading">
              Property Access Notes
            </Form.Label>
          </Col>
          <Col xs={6}>
            <Form.Control
              as="textarea"
              readOnly
              value={property.propertyAccessNotes}
            />
          </Col>
        </Row>
        {property.redFlag && (
          <Row className="mt-4">
            <Col xs={8}>
              <Alert variant="danger">
                Caution - This Property has a Red Flag set on it.
              </Alert>
            </Col>
          </Row>
        )}
        <Row className="mt-4">
          <Col xs={3}>
            <Form.Label className="form-label-heading">
              Work Order Reference
            </Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">Created</Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">Type</Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">Category</Form.Label>
          </Col>
        </Row>
        <Row className="mt-1">
          <Col xs={3}>
            <Form.Control type="text" readOnly value={workOrder.reference} />
          </Col>
          <Col xs={3}>
            <Form.Control
              type="text"
              readOnly
              value={
                workOrder.createdOn
                  ? moment(workOrder.createdOn).format("DD/MM/YYYY h:mm a")
                  : ""
              }
            />
          </Col>
          <Col xs={3}>
            <Form.Control type="text" readOnly value={workOrder.type} />
          </Col>
          <Col xs={3}>
            <Form.Control type="text" readOnly value={workOrder.category} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col xs={3}>
            <Form.Label className="form-label-heading">
              Work Item Reference
            </Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">Created</Form.Label>
          </Col>
          <Col xs={3}>
            <Form.Label className="form-label-heading">Category</Form.Label>
          </Col>
          <Col xs={3} />
        </Row>
        <Row className="mt-1">
          <Col xs={3}>
            <Form.Control type="text" readOnly value={workItem.reference} />
          </Col>
          <Col xs={3}>
            <Form.Control
              type="text"
              readOnly
              value={
                workItem.createdOn
                  ? moment(workItem.createdOn).format("DD/MM/YYYY h:mm a")
                  : ""
              }
            />
          </Col>
          <Col xs={3}>
            <Form.Control type="text" readOnly value={workItem.category} />
          </Col>
          <Col xs={3} />
        </Row>
        <Row className="mt-1">
          <Col>
            <div className="closed-work-item-summary-container">
              <WorkOrderActiveWorkItem
                workItem={workItem}
                mode="single"
                workOrder={workOrder}
              />
            </div>
          </Col>
        </Row>
        <Row className="mt-4 mb-2">
          <Col>
            <Form.Label className="form-label-heading">Forms</Form.Label>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col>
            <WorkItemForms workItem={workItem} />
          </Col>
        </Row>
        <Row className="mt-4 mb-4">
          <Col>
            <Link to={`/property/${property.id}/workorder/${workOrder.id}`}>
              <Button variant="secondary">Back to work order</Button>
            </Link>
          </Col>
        </Row>
      </Container>
    );
  return view;
}

export default WorkItem;
